import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../../Layout/Layout';
import {
  useGetPromos, useGetAllCompanyDevices, useGetPromoDesignLibrary, useGetQrLinks,
} from '../../../queries/promos';
import PromoList from './PromoList';
import PromoEditor from './PromoEditor';
import './promos.css';

/**
 * "عروضي" — two screens on real routes:
 *   /apps/promos        the list
 *   /apps/promos/new    the editor (new)         state.duplicateId = copy of an existing promo
 *   /apps/promos/:id    the editor (edit)
 * A reload keeps you on the editor and browser back returns to the list.
 *
 * The pure logic (runtime state, predictions, draft <-> payload) lives in
 * promosLogic.js and is re-exported here, so existing imports from './Promos'
 * keep working. Nothing on this page changes how or when a promo plays: it only
 * edits the same stored fields, plus the additive `template` JSON.
 */
export * from './promosLogic';

export default function Promos() {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.resolvedLanguage || i18n.language || '').startsWith('ar');
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const onNewRoute = location.pathname.replace(/\/$/, '').endsWith('/promos/new');
  const editing = onNewRoute || !!id;

  const { data: promosRaw, isLoading, isError, refetch } = useGetPromos();
  const promos = useMemo(() => (Array.isArray(promosRaw) ? promosRaw : []), [promosRaw]);
  const { data: devicesRaw } = useGetAllCompanyDevices();
  const devices = useMemo(() => (Array.isArray(devicesRaw?.data) ? devicesRaw.data : Array.isArray(devicesRaw) ? devicesRaw : []), [devicesRaw]);
  const { data: libraryRaw, isLoading: libLoading } = useGetPromoDesignLibrary();
  const libraryById = useMemo(() => {
    const arr = libraryRaw?.data ?? libraryRaw ?? [];
    return new Map((Array.isArray(arr) ? arr : []).map((m) => [m.id, m]));
  }, [libraryRaw]);
  const { data: qrRaw, isLoading: qrLoading } = useGetQrLinks();
  const qrLinks = useMemo(() => (Array.isArray(qrRaw) ? qrRaw : []), [qrRaw]);

  // Gated routes mount after /company/details resolves, so a reload first flashes NotFound, which leaves its
  // «صفحة غير موجودة» title behind (shown in the mobile header). Own the title while this page is up.
  useEffect(() => { document.title = `${t('promos.title')} · PopupPush`; }, [t]);

  // History rules (round 5). The list pushes the editor entry; leaving the editor must REMOVE that entry, never add another one:
  // entered from the list -> pop it (navigate(-1)); opened directly (reload / deep link, no marker) -> replace it with the list.
  // Before this, goList was a plain push, so الجدول -> عروضي -> editor -> back left [.., list, editor, list] and the list's back arrow popped onto the editor.
  const enteredFromList = !!location.state?.fromList;
  const goList = () => { if (enteredFromList) navigate(-1); else navigate('/apps/promos', { replace: true }); };

  // Layout fix scope: the phone save bar needs dynamic-viewport height on THIS page only (see promos.css `body.promos-open`).
  useEffect(() => { document.body.classList.add('promos-open'); return () => document.body.classList.remove('promos-open'); }, []);
  // Where the removed sub-bar's chevron led: one step back in history (same call as Navbar.handleBackClick), or the schedule hub when the page was opened directly.
  // BrowserRouter keeps `idx` in history.state: 0 = the first entry of this session (it survives a replace), so there is nothing earlier in the
  // app to return to. history.length is not usable (it also counts pages from before the app); location.key is only the fallback where idx is absent (tests).
  const canGoBack = () => { const idx = window.history.state?.idx; return typeof idx === 'number' ? idx > 0 : location.key !== 'default'; };
  const goBack = () => { if (canGoBack()) navigate(-1); else navigate('/schedule'); };
  const promo = id ? promos.find((p) => p.id === id) || null : null;
  const dupSeed = onNewRoute && location.state?.duplicateId ? promos.find((p) => p.id === location.state.duplicateId) || null : null;

  // Unknown id once the list has loaded -> back to the list (never an empty editor).
  useEffect(() => {
    if (id && !isLoading && !isError && !promo) navigate('/apps/promos', { replace: true });
  }, [id, isLoading, isError, promo, navigate]);

  const ready = !isLoading && !libLoading && !qrLoading;

  return (
    // The apps sub-bar (title + chevron) is dropped for this page, the same way Dashboard/Devices drop it (title ''): each screen
    // has its own header and its own single back control.
    <Layout subNavbarProps={{ title: '' }}>
      <ConfirmDialog />
      <div className="promos-shell" dir={isRtl ? 'rtl' : 'ltr'}>
        {editing ? (
          !ready || (id && !promo) ? (
            <div className="grid"><Skeleton height="18rem" borderRadius="16px" /></div>
          ) : (
            <PromoEditor
              key={id || (dupSeed ? `dup-${dupSeed.id}` : 'new')}
              promo={promo}
              seed={dupSeed ? { ...dupSeed, name: `${dupSeed.name}${t('promos.duplicate_suffix')}` } : null}
              promos={promos}
              devices={devices}
              libraryById={libraryById}
              qrLinks={qrLinks}
              onClose={goList}
              onSaved={goList}
            />
          )
        ) : (
          <PromoList
            promos={promos}
            devices={devices}
            libraryById={libraryById}
            libraryReady={!libLoading}
            qrLinks={qrLinks}
            isLoading={isLoading}
            isError={isError}
            refetch={refetch}
            onBack={goBack}
            onNew={() => navigate('/apps/promos/new', { state: { fromList: true } })}
            onEdit={(p) => navigate(`/apps/promos/${p.id}`, { state: { fromList: true } })}
            onDuplicate={(p) => navigate('/apps/promos/new', { state: { duplicateId: p.id, fromList: true } })}
          />
        )}
      </div>
    </Layout>
  );
}

